/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect} from "react";
import ReactDOM from "react-dom";
import {useSelector} from "react-redux";
import {useState} from "react";
import {convertToLocale} from "../helper/languageUtils";
import {PAYMENT_METHODS} from "../models/PaymentRequests";

export interface PaypalScript {
    PayPalButton: JSX.Element;
    PayPalFields: JSX.Element;
    scriptResolved: boolean;
    isPaymentMethodAvailable: (paymentMethod: string) => boolean;
    getFundingSource: (paymentMethod: string) => string;
}

export type Intent = "capture" | "authorize";

const usePayPalScript = (intent: Intent): PaypalScript => {

    const clientId = useSelector(state => state.cart.paypalClientId);
    const currency = useSelector(state => state.order?.totalBillingAmount?.currency?.code || "EUR");
    const moduleLanguage = useSelector(state => convertToLocale(state.config.moduleLanguage));

    const dataComplete = !!clientId && !!currency && !!moduleLanguage && !!intent;

    const scriptId = `hpm-paypal-script-${intent}`;
    const existingScript = document.getElementById(scriptId);

    const [resolved, setResolved] = useState(!!existingScript);

    const namespace = `hpm_paypal_${intent}`;
    const scriptURI = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency}&locale=${moduleLanguage}&intent=${intent}&components=buttons,payment-fields,marks,funding-eligibility&enable-funding=p24`;

    useEffect(() => {
        if(!resolved && dataComplete) {
            const script = document.createElement("script");
            script.src = scriptURI;
            script.id = scriptId;
            script.dataset["namespace"] = namespace;
            script.onload = () => {
                setResolved(true);
            };
            document.body.appendChild(script);
        }
    }, [dataComplete, namespace]);

    const script = window[namespace];

    const isButtonAvailable = () => {
        return resolved && script && script.Buttons;
    }

    const isFieldsAvailable = () => {
        return resolved && script && script.PaymentFields;
    }

    const isScriptFullyLoaded = () => {
        return resolved && script && script.Buttons && script.PaymentFields;
    }

    const isPaymentMethodAvailable = (paymentMethod: string) => {
        if(!resolved) {
            console.warn("usePayPalScript.isPaymentMethodAvailable: script not resolved yet");
            return false;
        }
        return script && script.isFundingEligible && script.isFundingEligible(getFundingSource(paymentMethod));
    }

    // https://developer.paypal.com/docs/checkout/standard/customize/standalone-buttons/
    const getFundingSource = (paymentMethod: string) => {
        switch (paymentMethod.toLowerCase()) {
            case PAYMENT_METHODS.PAY_LATER:
                return "paylater";
            case PAYMENT_METHODS.PRZELEWY24:
                return "p24";
            case PAYMENT_METHODS.CARD:
                return "card";
            case PAYMENT_METHODS.SEPA:
                return "sepa";
            default:
                return "paypal";
        }
    }

    const createButton = () => {
        if(!isButtonAvailable()) return null;
        return script.Buttons.driver("react", { React, ReactDOM });
    }

    const createFields = () => {
        if(!isFieldsAvailable()) return null;
        return script.PaymentFields.driver("react", { React, ReactDOM });
    }

    return {
        PayPalButton: createButton(),
        PayPalFields: createFields(),
        scriptResolved: isScriptFullyLoaded(),
        isPaymentMethodAvailable,
        getFundingSource,
        hasError: resolved && !isScriptFullyLoaded(),
    }
};

export default usePayPalScript;
